/**
 * CT-P1b: sequential Heikin-Ashi transform (must run BEFORE LOD bucketing).
 * Standard formulas; first bar seeds HAopen=open. Preserves x; prevClose
 * is intentionally dropped (KernelEngine re-derives it post-LOD).
 */
export function toHeikinAshiCandles(data) {
    const result = [];
    let prevHaOpen = 0;
    let prevHaClose = 0;
    for (let i = 0; i < data.length; i++) {
        const bar = data[i];
        const haClose = (bar.open + bar.high + bar.low + bar.close) / 4;
        const haOpen = i === 0 ? bar.open : (prevHaOpen + prevHaClose) / 2;
        result.push({
            x: bar.x,
            open: haOpen,
            high: Math.max(bar.high, haOpen, haClose),
            low: Math.min(bar.low, haOpen, haClose),
            close: haClose,
            visible: bar.visible,
        });
        prevHaOpen = haOpen;
        prevHaClose = haClose;
    }
    return result;
}
const DEFAULT_UP_COLOR = "#6BA583";
const DEFAULT_DOWN_COLOR = "#FF0000";
function resolveColor(accessor, datum, isUp, index, fallback) {
    if (typeof accessor === "function") {
        return accessor(datum, isUp, index) ?? fallback;
    }
    if (typeof accessor === "string") {
        return accessor;
    }
    return fallback;
}
function toRoundedPixel(value) {
    return Math.round(value);
}
export class CandlestickRenderer {
    render(ctx, data, options) {
        if (data.length === 0) {
            return;
        }
        const mode = options.mode ?? "candlestick";
        const barWidth = Math.max(1, options.barWidth ?? 6);
        // Z3-01 (D-Z05 v2): unified fractional X — body, offsets and wick share one
        // coordinate system so slow zoom/pan never separates the wick from the body
        // (wick jitter). Only the PRICE axis (Y) stays pixel-snapped for crispness.
        const halfWidth = barWidth / 2;
        const strokeWidth = Math.max(1, options.strokeWidth ?? 1);
        // strokeRect draws the stroke centered on the rect edge, expanding 0.5px outside
        // the rect boundary on each side. To keep visual candle body width equal to volume
        // bar fillRect at the same barWidth, inset the body rect by strokeWidth (1px each side).
        const bodyInset = barWidth >= 3 ? strokeWidth : 0;
        const insetBarWidth = Math.max(1, barWidth - bodyInset * 2);
        const insetHalfWidth = insetBarWidth / 2;
        const opacity = options.opacity ?? 1;
        const xAccessor = options.xAccessor ?? ((datum) => datum.x);
        const openAccessor = options.openAccessor ?? ((datum) => datum.open);
        const highAccessor = options.highAccessor ?? ((datum) => datum.high);
        const lowAccessor = options.lowAccessor ?? ((datum) => datum.low);
        const closeAccessor = options.closeAccessor ?? ((datum) => datum.close);
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.lineWidth = strokeWidth;
        ctx.setLineDash([]);
        for (let index = 0; index < data.length; index += 1) {
            const datum = data[index];
            if (datum.visible === false) {
                continue;
            }
            const x = xAccessor(datum, index);
            const open = openAccessor(datum, index);
            const high = highAccessor(datum, index);
            const low = lowAccessor(datum, index);
            const close = closeAccessor(datum, index);
            if (![x, open, high, low, close].every(Number.isFinite)) {
                continue;
            }
            const isUp = close >= open;
            const prevClose = typeof datum.prevClose === "number" && Number.isFinite(datum.prevClose)
                ? datum.prevClose
                : null;
            // CT-P1a (TradingView 4-state): hollow/OHLC direction follows prevClose;
            // hollowness still follows the session; legacy fallback when absent.
            const colorUp = (mode === "hollow" || mode === "ohlc") && prevClose !== null
                ? close >= prevClose
                : isUp;
            const directionColor = colorUp
                ? (options.upColor ?? DEFAULT_UP_COLOR)
                : (options.downColor ?? DEFAULT_DOWN_COLOR);
            const wickColor = resolveColor(options.wickStroke, datum, isUp, index, directionColor);
            const strokeColor = resolveColor(options.stroke, datum, isUp, index, directionColor);
            const fillColor = resolveColor(options.fill, datum, isUp, index, directionColor);
            const openY = options.yScale(open);
            const highY = options.yScale(high);
            const lowY = options.yScale(low);
            const closeY = options.yScale(close);
            if (![openY, highY, lowY, closeY].every(Number.isFinite)) {
                continue;
            }
            const bodyTop = toRoundedPixel(Math.min(openY, closeY));
            const bodyBottom = toRoundedPixel(Math.max(openY, closeY));
            const wickTop = toRoundedPixel(Math.min(highY, lowY));
            const wickBottom = toRoundedPixel(Math.max(highY, lowY));
            const bodyHeight = Math.max(1, bodyBottom - bodyTop);
            const left = x - halfWidth;
            const bodyLeft = x - insetHalfWidth;
            // Wick shares the fractional body center (Z3-01): rounding it
            // independently would offset it up to ~0.8px and jump a full pixel
            // whenever bodyCenterX crosses .5 during slow zoom/pan.
            const bodyCenterX = bodyLeft + insetBarWidth / 2;
            ctx.strokeStyle = wickColor;
            ctx.beginPath();
            const wickX = bodyCenterX;
            ctx.moveTo(wickX, wickTop);
            ctx.lineTo(wickX, wickBottom);
            ctx.stroke();
            if (mode === "ohlc") {
                ctx.strokeStyle = strokeColor;
                ctx.beginPath();
                ctx.moveTo(left, toRoundedPixel(openY));
                ctx.lineTo(x, toRoundedPixel(openY));
                ctx.moveTo(x, toRoundedPixel(closeY));
                ctx.lineTo(x + halfWidth, toRoundedPixel(closeY));
                ctx.stroke();
                continue;
            }
            ctx.strokeStyle = strokeColor;
            if (mode === "hollow" && isUp) {
                // hollow candle: stroke only, use inset so outer edge aligns with volume bar
                ctx.strokeRect(bodyLeft, bodyTop, insetBarWidth, bodyHeight);
                continue;
            }
            ctx.fillStyle = fillColor;
            // inset body so stroke outer edge aligns with volume bar fillRect at same barWidth
            ctx.fillRect(bodyLeft, bodyTop, insetBarWidth, bodyHeight);
            ctx.strokeRect(bodyLeft, bodyTop, insetBarWidth, bodyHeight);
        }
        ctx.restore();
    }
}
